import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Alert } from 'react-bootstrap';
import { IconButton, TextField, Tooltip, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import TanstackTable from 'components/elements/advance-table/TanstackTable';
import RefreshButton from 'components/elements/refresh-button/RefreshButton';
import { getGeneratedContentsAPI } from 'ServerAPI/serverAPI';
import actionIcon from "../../assets/images/course/action-icon.svg";
import { useToast } from 'context/toast-context/ToastProvider';
import { DateDisplay } from 'helper/utils';

// DocumentGenerationTable Component
export const DocumentGenerationTable = ({ userId , setDocumentCount }) => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // State management
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Fetch data function
    const fetchData = async (isRefresh = false) => {
        try {
            if (isRefresh) {
                setIsRefreshing(true);
            } else {
                setLoading(true);
            }
            setError(null);

            if (!userId) {
                showToast('User ID not found in cookies', "error");
                return;
            }

            const response = await getGeneratedContentsAPI(userId);

            if (response.data && response.data.content) {
                setData(response.data.content);
                setDocumentCount((response.data.content).length);
            } else {
                setData([]);
            }
        } catch (err) {
            console.error('Error fetching generated contents:', err);
            setError(err.message || 'Failed to fetch generated contents');
            setData([]);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    // Initial data fetch
    useEffect(() => {
        fetchData();
    }, [userId]);

    // Handle refresh
    const handleRefresh = () => {
        fetchData(true);
    };

    // Handle search
    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    // Handle navigation to new content generation
    const handleGenerateNewDocument = () => {
        navigate('/content-generation/');
    };

    // Handle view content
    const handleViewContent = (generatedContentId) => {
        navigate('/content-generation/', {
            state: {
                userId: userId,
                generatedContentId: generatedContentId
            }
        });
    };

    // Filter data based on search term
    const filteredData = useMemo(() => {
        if (!searchTerm) return data;

        return data.filter(item =>
            item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.domain?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [data, searchTerm]);

    // Table columns configuration
    const columns = useMemo(() => [
        {
            id: 'sno',
            header: 'S.No.',
            cell: ({ row }) => row.index + 1,
            enableSorting: false,
        },
        {
            accessorKey: 'title',
            header: 'Title',
            cell: ({ getValue }) => (
                <div className="fw-semibold text-truncate" style={{ maxWidth: '200px' }}>
                    {getValue() || 'N/A'}
                </div>
            ),
        },
        {
            accessorKey: 'department',
            header: 'Department',
            cell: ({ getValue }) => (
                <span className="text-muted">
                    {getValue() || 'N/A'}
                </span>
            ),
        },
        {
            accessorKey: 'domain',
            header: 'Domain',
            cell: ({ getValue }) => (
                <span className="badge bg-secondary">
                    {getValue() || 'N/A'}
                </span>
            ),
        },
        {
            accessorKey: 'description',
            header: 'Description',
            cell: ({ getValue }) => (
                <div className="text-truncate" style={{ maxWidth: '250px' }}>
                    {getValue() || 'N/A'}
                </div>
            ),
        },
        {
            accessorKey: 'createdAt',
            header: 'Created At',
            cell: ({ getValue }) => (
                <span className="text-muted small">
                    <DateDisplay timeString={getValue()} />
                </span>
            ),
        },
        {
            accessorKey: 'actions',
            header: 'Actions',
            cell: ({ row }) => (
                <Tooltip title="View Content">
                    <IconButton>
                        <span
                            onClick={() => {
                                handleViewContent(row.original.generatedContentId);
                            }}
                            style={{ display: 'flex', alignItems: 'center' }}
                        >
                            <img src={actionIcon} alt="action icon" />
                        </span>
                    </IconButton>
                </Tooltip>
            ),
            enableSorting: false,
        },
    ], [userId]);

    // Render loading state
    if (loading && !isRefreshing) {
        return (
            <div className="text-center p-5">
                <CircularProgress />
                <div className="mt-2">Loading documents...</div>
            </div>
        );
    }

    return (
        <>
            {/* Search and Action Bar */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search for title, department, domain, description"
                    InputProps={{ startAdornment: <SearchIcon /> }}
                    onChange={handleSearch}
                    value={searchTerm}
                    className="w-100"
                    style={{ maxWidth: '400px' }}
                />
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <RefreshButton
                        handleRefresh={handleRefresh}
                        title="Refresh Documents"
                        isRefreshing={isRefreshing}
                    />
                    <Button
                        variant="primary"
                        onClick={handleGenerateNewDocument}
                        className="d-flex align-items-center gap-1"
                    >
                        <AddIcon fontSize="small" />
                        Generate New Document
                    </Button>
                </div>
            </div>

            {/* Error State */}
            {error && (
                <Alert variant="danger" className="mb-4">
                    <Alert.Heading>Error</Alert.Heading>
                    <p className="mb-0">{error}</p>
                    <hr />
                    <div className="d-flex justify-content-end">
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => fetchData()}
                        >
                            Try Again
                        </Button>
                    </div>
                </Alert>
            )}

            {/* Content Area */}
            <div className="mt-3">
                {!error && (
                    <>
                        {filteredData.length === 0 ? (
                            <div className="text-center p-5">
                                <div className="mb-3">
                                    <i className="fas fa-file-alt fa-3x text-muted"></i>
                                </div>
                                <h5 className="text-muted">
                                    {searchTerm ? 'No documents found matching your search' : 'No generated documents found'}
                                </h5>
                                <p className="text-muted">
                                    {searchTerm
                                        ? 'Try adjusting your search terms or clear the search to see all documents.'
                                        : 'Get started by generating your first document.'
                                    }
                                </p>
                                {!searchTerm && (
                                    <Button
                                        variant="primary"
                                        onClick={handleGenerateNewDocument}
                                        className="mt-2"
                                    >
                                        <AddIcon fontSize="small" className="me-1" />
                                        Generate New Document
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <TanstackTable
                                data={filteredData}
                                columns={columns}
                                pagination={true}
                            />
                        )}
                    </>
                )}
            </div>
        </>
    );
};